import { type ChangeEvent, type DragEvent, useEffect, useRef, useState } from "react";
import UploadIcon from "@/assets/icons/upload.svg?react";
import Avatar from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { useToast } from "@/components/ui/use-toast";
import useScreens from "@/hooks/useScreens";
import { cn } from "@/lib/utils";

type ImageUploaderProps = {
  className?: string;
  avatarUrl?: string | null;
  setFile: (value: { file: any; size: any; name: any; type: any }) => void;
  withPreview?: boolean;
  description?: string;
  fileTypesRegex?: RegExp;
  inputAcceptFiles?: string;
  disabled?: boolean;
  maxFileSizeMB?: number;
};

export default function FileUploader({
  className,
  avatarUrl,
  setFile,
  withPreview = true,
  fileTypesRegex = /\.(jpe?g|png|gif)$/i,
  inputAcceptFiles = "image/png, image/jpeg, image/gif, image/svg+xml",
  disabled = false,
  maxFileSizeMB = 15,
  description,
}: ImageUploaderProps) {
  const [avatar, setAvatar] = useState("");
  const input = useRef<HTMLInputElement | null>(null);
  const isTabletOrAbove = useScreens("md", "up");
  const { toast } = useToast();

  useEffect(() => {
    if (avatarUrl) {
      setAvatar(avatarUrl);
    }
  }, [avatarUrl]);

  const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;
  const finalDescription = description || `PNG, JPG or GIF (max. ${maxFileSizeMB}MB)`;

  const handleLoadFile = (uploadedFile: File) => {
    if (!fileTypesRegex.test(uploadedFile.name)) return;

    if (uploadedFile.size > maxFileSizeBytes) {
      toast({
        variant: "destructive",
        title: "File is too big",
        description: `File is too large. Maximum allowed size is ${maxFileSizeMB}MB.`,
      });
      return;
    }

    const blobURL = URL.createObjectURL(uploadedFile);
    setAvatar(blobURL);

    setFile({
      file: uploadedFile,
      size: uploadedFile.size,
      name: uploadedFile.name,
      type: uploadedFile.type,
    });
  };

  const handleLoadFiles = (uploadedFiles: FileList | null) => {
    if (!uploadedFiles || disabled) return;

    Array.from(uploadedFiles).forEach(handleLoadFile);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();
    handleLoadFiles(event.dataTransfer.files);
  };

  const handleChangeFile = (event: ChangeEvent<HTMLInputElement>) => {
    handleLoadFiles(event.target.files);
    if (input.current) {
      input.current.value = "";
    }
  };

  return (
    <div className={cn("flex flex-col gap-5 pb-6 md:flex-row", className)}>
      {withPreview && <Avatar url={avatar} size={64} className="min-w-[64px] self-center md:self-start" />}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: drop zone is a passive drag target; the actual click affordance is the Button below */}
      <div
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className="flex w-full flex-col items-center rounded-xl border border-gray-200 border-solid py-4"
      >
        <div className="cursor-pointer rounded-lg border border-gray-200 border-solid p-3">
          <UploadIcon />
        </div>
        <div>
          <input
            disabled={disabled}
            type="file"
            multiple={!withPreview}
            onChange={handleChangeFile}
            accept={inputAcceptFiles}
            className="hidden"
            ref={input}
          />
          <Button
            onClick={() => input.current?.click()}
            variant="ghost"
            className="mr-1 inline h-[20px] cursor-pointer p-0 text-primary-700 hover:bg-white"
          >
            {disabled ? "Disabled" : "Click to upload"}
          </Button>
          {isTabletOrAbove && (
            <Typography className="text-gray-600" variant="text-small" component="span">
              {!disabled && "or drag and drop"}
            </Typography>
          )}
        </div>
        <Typography className="text-gray-600" variant="text-small">
          {!disabled && finalDescription}
        </Typography>
      </div>
    </div>
  );
}
